import { fetchAuthSession } from 'aws-amplify/auth';

/**
 * Owner-scoping helpers for admin-facing record lookups.
 *
 * Amplify stores the `owner` value inconsistently across write paths:
 *   - Frontend user-pool writes (no explicit owner) → composite `${sub}::${cognito:username}`
 *   - Backend IAM writes (mcpProxy sets `owner: username`) → bare `cognito:username`
 *   - `Username === sub` for native Cognito users; Google users get `google_<id>`.
 *
 * So a reliable owner filter matches against ALL candidate formats rather than
 * guessing one. See the Admin User Records plan and issue #673.
 */
export const buildOwnerCandidates = (
  sub?: string | null,
  username?: string | null,
): string[] => {
  const candidates = new Set<string>();
  if (sub) candidates.add(sub);
  if (username) candidates.add(username);
  if (sub && username) candidates.add(`${sub}::${username}`);
  return Array.from(candidates);
};

/**
 * Build a GraphQL filter fragment that matches any of the owner candidates,
 * e.g. `{ or: [{ owner: { eq: a } }, { owner: { eq: b } }] }`.
 * (ModelIDInput has no `in`, so we OR across `eq`.)
 */
export const ownerOrFilter = (candidates: string[]) => ({
  or: candidates.map((c) => ({ owner: { eq: c } })),
});

interface AssetGroupOwnerShape {
  owner?: string | null;
}

interface UserStateShape {
  details?: { groups?: string[] | null } | null;
  permissionDetails?: { id?: string | null } | null;
}

/**
 * Whether the current user may edit an asset group: Admins, or the group's owner.
 * Mirrors the rule used on the groups list page (AssetGroupFolder).
 */
export const canEditAssetGroup = (
  user?: UserStateShape | null,
  assetGroup?: AssetGroupOwnerShape | null,
): boolean => {
  if (user?.details?.groups?.includes('Admin')) return true;
  const currentUserId = user?.permissionDetails?.id;
  return Boolean(currentUserId && assetGroup?.owner === currentUserId);
};

/**
 * Owner candidates for the CURRENT user regardless of role. Used to query
 * owner-keyed GSIs (e.g. conversationsByOwner) once per stored owner format.
 */
export const getCurrentUserOwnerCandidates = async (): Promise<string[]> => {
  try {
    const session = await fetchAuthSession();
    const payload = session?.tokens?.idToken?.payload as
      | Record<string, unknown>
      | undefined;
    const sub = payload?.sub as string | undefined;
    const username = payload?.['cognito:username'] as string | undefined;
    return buildOwnerCandidates(sub, username);
  } catch (error) {
    console.error('[getCurrentUserOwnerCandidates] error resolving identity', error);
    return [];
  }
};

/**
 * The composite `${sub}::${cognito:username}` claim Amplify would stamp as `owner` on a
 * frontend create — for services that must set it explicitly. The create resolver skips
 * auto-population when the caller was already authorized by a static group rule (Admin),
 * so an Admin's rows would otherwise have no owner. Null when identity can't be resolved.
 */
export const getCurrentOwnerClaim = async (): Promise<string | null> => {
  try {
    const session = await fetchAuthSession();
    const payload = session?.tokens?.idToken?.payload as
      | Record<string, unknown>
      | undefined;
    const sub = payload?.sub as string | undefined;
    const username = payload?.['cognito:username'] as string | undefined;
    return sub && username ? `${sub}::${username}` : null;
  } catch (error) {
    console.error('[getCurrentOwnerClaim] error resolving identity', error);
    return null;
  }
};

/**
 * Returns owner candidates for the CURRENT user if they are an admin
 * (`Admin`/`AdminReadOnly`), otherwise `null`.
 *
 * Used to scope admin-facing lists (e.g. the ai-analyst sidebar) that would
 * otherwise leak every user's records once a model grants the Admin group
 * global read. Non-admins get `null` → callers leave their query untouched.
 */
export const getCurrentUserAdminOwnerScope = async (): Promise<string[] | null> => {
  try {
    return await resolveAdminOwnerScope();
  } catch (error) {
    console.error('[getCurrentUserAdminOwnerScope] error resolving identity', error);
    return null;
  }
};

// Throws on an auth failure, so a caller can tell "not an admin" apart from "could not tell"
const resolveAdminOwnerScope = async (): Promise<string[] | null> => {
  const session = await fetchAuthSession();
  const payload = session?.tokens?.idToken?.payload as
    | Record<string, unknown>
    | undefined;
  const groups = (payload?.['cognito:groups'] as string[] | undefined) ?? [];
  const isAdmin = groups.includes('Admin') || groups.includes('AdminReadOnly');
  if (!isAdmin) return null;

  const sub = payload?.sub as string | undefined;
  const username = payload?.['cognito:username'] as string | undefined;
  return buildOwnerCandidates(sub, username);
};

/** Owner filter for an admin's list query, or undefined for non-admins; throws if identity cannot be resolved. */
export const getAdminOwnerFilter = async (): Promise<
  { or: Array<{ owner: { eq: string } }> } | undefined
> => {
  // No swallowed error here: a failed lookup read as "not an admin" would load every org's records unfiltered
  const candidates = await resolveAdminOwnerScope();
  return candidates && candidates.length > 0 ? ownerOrFilter(candidates) : undefined;
};

/**
 * The current user's Cognito groups from the session's ID token, or [] when it can't be read. Read directly
 * rather than from the user store, which a hard reload fills in only after the page has rendered.
 */
export const getCurrentUserGroups = async (): Promise<string[]> => {
  try {
    const session = await fetchAuthSession();
    const groups = session?.tokens?.idToken?.payload?.['cognito:groups'];
    return Array.isArray(groups) ? groups.map(String) : [];
  } catch (error) {
    console.error('[getCurrentUserGroups] error resolving identity', error);
    return [];
  }
};
